/**
 * Structural slice of SelectSaleConfigurationValues — kept local so this
 * leaf module never imports from the dialog component (which pulls in the
 * store and would put a cycle through the merge-tags graph).
 */
type TableFontSizeConfiguration = {
  font_size?: string | number | null;
  totals_font_size?: string | number | null;
};

/**
 * User-selectable font size for the configurable invoice merge tag table
 * ({{Patient.SelectedInvoice.WithPredefinedParameters(...)}}).
 *
 * The chosen size travels inside the merge tag options string as
 * `font_size=<px>` — the only state that survives re-resolution, template
 * round-trips, and the placeholder <-> table swaps — and is emitted as inline
 * `font-size` on every cell. Both PDF paths honor inline px sizes: the
 * standard react-pdf-html path passes numeric px through, and Enhanced Table
 * Rendering lifts the per-<td> value into `fontSizePx`
 * (pdf-document-table.component.tsx), so text always wraps inside its own
 * cell at any size.
 */

export const AUTO_TABLE_FONT_SIZE_VALUE = "auto";

export const MIN_TABLE_FONT_SIZE_PX = 8;
export const MAX_TABLE_FONT_SIZE_PX = 16;

/**
 * The pre-existing auto-shrink ladder: the more columns the table has, the
 * smaller the type, so a fully loaded table still fits the page.
 */
export const getAutoTableFontSizePx = (columnCount: number): number => {
  if (columnCount >= 20) return 9;
  if (columnCount >= 19) return 10;
  if (columnCount >= 16) return 11;
  return 12;
};

/**
 * Ceiling for an explicit choice, by column count. A letter page gives a
 * 20+-column table roughly 25pt per column; a money value at 14px needs more
 * than double that, and react-pdf paints overflowing tokens across the
 * neighboring cells (or hyphen-breaks them mid-number once they can wrap).
 *
 * The Define Parameters dialog constrains its picker to this ceiling and
 * snaps the selection when added columns invalidate it, so the size a user
 * can choose is always the size that renders. The render-time clamp in
 * resolveTableFontSizePx exists only as a backstop for hand-edited tokens.
 */
export const getMaxExplicitTableFontSizePx = (columnCount: number): number => {
  // Never cap below what Auto would pick for the same column count.
  if (columnCount >= 19)
    return Math.max(getAutoTableFontSizePx(columnCount), 9);
  if (columnCount >= 16) return 11;
  if (columnCount >= 13) return 12;
  if (columnCount >= 11) return 13;
  if (columnCount >= 9) return 14;
  return MAX_TABLE_FONT_SIZE_PX;
};

/**
 * Parses an explicit `font_size` option value (string from the token parser
 * or number from the dialog form). Anything absent, non-numeric, or outside
 * the allowed 8–16px range returns undefined so the caller falls back to
 * Auto — a hand-edited token can never produce an unreadable table.
 */
export const parseTableFontSizePx = (
  value: string | number | null | undefined
): number | undefined => {
  if (value === null || value === undefined || value === "") return undefined;
  if (value === AUTO_TABLE_FONT_SIZE_VALUE) return undefined;

  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed)) return undefined;
  if (parsed < MIN_TABLE_FONT_SIZE_PX || parsed > MAX_TABLE_FONT_SIZE_PX) {
    return undefined;
  }

  return parsed;
};

/**
 * Effective size for line item and header cells: an explicit valid choice
 * wins (clamped to the per-column-count ceiling); otherwise the column-count
 * ladder (today's behavior).
 */
export const resolveTableFontSizePx = (
  saleConfiguration: TableFontSizeConfiguration | undefined,
  columnCount: number
): number => {
  const explicit = parseTableFontSizePx(saleConfiguration?.font_size);
  if (explicit === undefined) return getAutoTableFontSizePx(columnCount);
  return Math.min(explicit, getMaxExplicitTableFontSizePx(columnCount));
};

// ENG-2462: the totals rows' own size, chosen in the Define Parameters dialog and saved in the merge tag
// as `totals_font_size`. Absent is "standard": 14px, the size the PDF has always printed totals at.
export const STANDARD_TOTALS_FONT_SIZE_VALUE = "standard";
export const STANDARD_TOTALS_FONT_SIZE_PX = 14;
// Follow the table body's size instead.
export const TABLE_TOTALS_FONT_SIZE_VALUE = "table";

/**
 * Effective size for the totals rows: the table body's size for "table", an explicit 8–16px choice, or
 * the standard 14px for anything else (absent, "standard", or a hand-edited value out of range).
 */
export const resolveTotalsFontSizePx = (
  saleConfiguration: TableFontSizeConfiguration | undefined,
  columnCount: number
): number => {
  const choice = saleConfiguration?.totals_font_size;
  if (choice === TABLE_TOTALS_FONT_SIZE_VALUE) {
    return resolveTableFontSizePx(saleConfiguration, columnCount);
  }
  return parseTableFontSizePx(choice) ?? STANDARD_TOTALS_FONT_SIZE_PX;
};

/**
 * Effective size for the discount strikethrough/caption spans inside the
 * Price cell. Historically pinned at 11px; with an explicit base the caption
 * sits one step below the (capped) body size so it still reads as secondary
 * to the price.
 */
export const resolveCaptionFontSizePx = (
  saleConfiguration: TableFontSizeConfiguration | undefined,
  columnCount: number
): number => {
  const explicit = parseTableFontSizePx(saleConfiguration?.font_size);
  if (explicit === undefined) return 11;
  return Math.max(
    resolveTableFontSizePx(saleConfiguration, columnCount) - 1,
    MIN_TABLE_FONT_SIZE_PX - 1
  );
};
